import { useAtomValue, useSetAtom } from "jotai";
import { Activity, Bug, Database, Info, RefreshCw, RotateCw, Settings } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { Device } from "@/api/model/device";
import type { LicState } from "@/api/model/lic-state";
import type { PropertyDeviceSensorAssignment } from "@/api/model/property-device-sensor-assignment";
import type { SensorModel } from "@/api/model/sensor-model";
import { apiService } from "@/api";
import Button from "@/components/ui/Button";
import {
  ensureValidPlanSchedule,
  findAllPlanConflicts,
  type PlanScheduleLike,
} from "@/pages/main/utils/irrigationPlanOverlap";
import {
  latestLICPacketByDeviceAndTypeAtom,
  licStateByDeviceIdAtom,
  plansByLicIdAtom,
  updatePropertyDeviceAtom,
} from "@/store";
import { propertyDevicesAtom } from "@/store/data";
import { appConfig } from "@/utils/config";
import { formatDays } from "@/utils/format";
import { Modal, useToast } from ".";
import Toggle from "./ui/Toggle";

interface AdvanceCodekConfigProps {
  trigger: (open: () => void) => React.ReactNode;
  device: Device;
  enablePolling?: boolean;
}

interface PublishRawFlags {
  devOthers: boolean;
  devStatus: boolean;
  devConfig: boolean;
  devReset: boolean;
}

const decodePublishRawFlags = (value: number): PublishRawFlags => ({
  devOthers: (value & 1) !== 0,
  devStatus: (value & 2) !== 0,
  devConfig: (value & 4) !== 0,
  devReset: (value & 8) !== 0,
});

/** Draft for the analog-input sensor assignment. */
interface SensorDraft {
  /** Selected sensor_model id; "" means no sensor (disabled). */
  sensorModelId: string;
  deviceInputNumber: number;
  sensorIdentifier: string;
  useOverride: boolean;
  conversionParameters: string;
  conversionFormula: string;
  conversionUnit: string;
}

const EMPTY_SENSOR_DRAFT: SensorDraft = {
  sensorModelId: "",
  deviceInputNumber: 1,
  sensorIdentifier: "",
  useOverride: false,
  conversionParameters: "",
  conversionFormula: "",
  conversionUnit: "",
};

/** The CODEC has a single analog input; it is always input #1. */
const CODEC_ANALOG_INPUT_NUMBER = 1;

interface OptionChanges {
  publish_raw_flags: PublishRawFlags;
  debug: boolean;
  enable_schedule_resumption: boolean;
  enable_ferti_resumption: boolean;
  max_resumption_attempts: number;
}

function AdvanceCodekConfig({ trigger, device, enablePolling = false }: AdvanceCodekConfigProps) {
  const { showSuccess, showError } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // State for conflicts modal when enabling resumption
  const [showConflictsModal, setShowConflictsModal] = useState(false);
  const [conflictsDetails, setConflictsDetails] = useState<{
    gapWithResumption: number;
    conflicts: Array<{
      planA: string;
      planB: string;
      day: string;
      gapMinutes: number;
    }>;
    extra: number;
  } | null>(null);

  // Sensor assignment state
  const [sensorModels, setSensorModels] = useState<SensorModel[]>([]);
  const [activeAssignment, setActiveAssignment] = useState<PropertyDeviceSensorAssignment | null>(null);
  const [sensorDraft, setSensorDraft] = useState<SensorDraft>(EMPTY_SENSOR_DRAFT);
  const [sensorError, setSensorError] = useState<string | null>(null);
  const [sensorLoading, setSensorLoading] = useState(false);

  const latestLICPacketByDeviceAndType = useAtomValue(latestLICPacketByDeviceAndTypeAtom);
  const licState = useAtomValue(licStateByDeviceIdAtom);
  const updatePropertyDevice = useSetAtom(updatePropertyDeviceAtom);
  const propertyDevices = useAtomValue(propertyDevicesAtom);
  const plansByLicId = useAtomValue(plansByLicIdAtom);

  const latestStatusPacket = useMemo(
    () => latestLICPacketByDeviceAndType(device.id, "info"),
    [device.id, latestLICPacketByDeviceAndType]
  );

  const deviceId = device.id;
  const licStateForDevice = useMemo(() => (licState ? licState(deviceId) : null), [licState, deviceId]);
  const licStateConfig = licStateForDevice?.lic?.config;
  const propertyDevice = propertyDevices.find((pd) => pd.device.id === deviceId);
  const metadataConfig = propertyDevice?.metadata ? propertyDevice.metadata : null;
  const propertyDeviceId = licStateForDevice?.lic?.propertyDeviceId ?? null;

  const sourceConfig =
    metadataConfig?.id == null
      ? latestStatusPacket?.payload_data?.id == null
        ? null
        : licStateConfig
      : latestStatusPacket?.payload_data?.id == null
        ? metadataConfig
        : metadataConfig.id > latestStatusPacket.payload_data.id
          ? metadataConfig
          : licStateConfig;

  const encodePublishRawFlags = (flags: PublishRawFlags): number => {
    let value = 0;
    if (flags.devOthers) value |= 1;
    if (flags.devStatus) value |= 2;
    if (flags.devConfig) value |= 4;
    if (flags.devReset) value |= 8;
    return value;
  };

  const [draftConfig, setDraftConfig] = useState<OptionChanges>(() => ({
    publish_raw_flags: decodePublishRawFlags(sourceConfig?.publish_raw_data ?? 0),
    debug: sourceConfig?.debug ?? false,
    enable_schedule_resumption: sourceConfig?.enable_schedule_resumption ?? false,
    enable_ferti_resumption: sourceConfig?.enable_ferti_resumption ?? false,
    max_resumption_attempts: sourceConfig?.max_resumption_attempts ?? 2,
  }));

  // Load sensor models + the active input-1 assignment whenever the modal opens
  // for a known property device. Sensor config is queryable via the Directus API
  // (no metadata JSON), fixing the JSON-operator blocker.
  useEffect(() => {
    if (!isOpen || !propertyDeviceId) {
      return;
    }
    let cancelled = false;
    const load = async () => {
      setSensorLoading(true);
      try {
        const models = await apiService.sensorModel.query({ fields: ["*"], limit: -1, sort: ["type", "model"] });
        if (cancelled) return;
        setSensorModels(models);

        const assignments = await apiService.propertyDeviceSensorAssignment.query({
          fields: ["*"],
          filter: { property_device: { _eq: propertyDeviceId } },
        });
        if (cancelled) return;
        const active = assignments.find(
          (a) => a.device_input_number === CODEC_ANALOG_INPUT_NUMBER && a.end_date == null
        );
        setActiveAssignment(active ?? null);
        setSensorDraft(buildDraftFromAssignment(active ?? null, models));
        setSensorError(null);
      } catch (err) {
        console.error("Erro ao carregar sensores:", err);
      } finally {
        if (!cancelled) setSensorLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, propertyDeviceId]);

  const selectedSensorModel = useMemo(
    () => sensorModels.find((m) => m.id === sensorDraft.sensorModelId) ?? null,
    [sensorModels, sensorDraft.sensorModelId]
  );

  // Effective conversion = assignment override ?? sensor_model default.
  const effectiveConversion = useMemo(() => {
    if (!sensorDraft.sensorModelId) return null;
    const params = sensorDraft.useOverride && sensorDraft.conversionParameters.trim()
      ? safeParseJson(sensorDraft.conversionParameters)
      : selectedSensorModel?.conversion_parameters ?? null;
    const formula = sensorDraft.useOverride && sensorDraft.conversionFormula.trim()
      ? sensorDraft.conversionFormula
      : selectedSensorModel?.conversion_formula ?? null;
    const unit = sensorDraft.useOverride && sensorDraft.conversionUnit.trim()
      ? sensorDraft.conversionUnit
      : selectedSensorModel?.conversion_unit ?? null;
    return { parameters: params, formula, unit };
  }, [sensorDraft, selectedSensorModel]);

  const hasChanges = useMemo(() => {
    if (!sourceConfig) return false;
    const currentFlags = decodePublishRawFlags(sourceConfig.publish_raw_data ?? 0);
    return (
      draftConfig.publish_raw_flags.devOthers !== currentFlags.devOthers ||
      draftConfig.publish_raw_flags.devStatus !== currentFlags.devStatus ||
      draftConfig.publish_raw_flags.devConfig !== currentFlags.devConfig ||
      draftConfig.publish_raw_flags.devReset !== currentFlags.devReset ||
      draftConfig.debug !== !!sourceConfig.debug ||
      draftConfig.enable_schedule_resumption !== !!sourceConfig.enable_schedule_resumption ||
      draftConfig.enable_ferti_resumption !== !!sourceConfig.enable_ferti_resumption ||
      draftConfig.max_resumption_attempts !== sourceConfig.max_resumption_attempts
    );
  }, [draftConfig, sourceConfig]);

  const sensorHasChanges = useMemo(() => {
    return !draftsEqual(sensorDraft, buildDraftFromAssignment(activeAssignment, sensorModels));
  }, [sensorDraft, activeAssignment, sensorModels]);

  const changeToggleOptionState = (key: keyof OptionChanges) => {
    setDraftConfig((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const togglePublishRawFlag = (key: keyof PublishRawFlags) => {
    setDraftConfig((prev) => ({
      ...prev,
      publish_raw_flags: {
        ...prev.publish_raw_flags,
        [key]: !prev.publish_raw_flags[key],
      },
    }));
  };

  const handleMaxAttemptsChange = (value: number) => {
    setDraftConfig((prev) => ({
      ...prev,
      max_resumption_attempts: value,
    }));
  };

  const publishRawOptions = [
    {
      id: "devOthers",
      label: "Outros pacotes",
      icon: <Database className="w-4 h-4" />,
      checked: draftConfig.publish_raw_flags.devOthers,
      onChange: () => togglePublishRawFlag("devOthers"),
    },
    {
      id: "devStatus",
      label: "Status do dispositivo",
      icon: <Database className="w-4 h-4" />,
      checked: draftConfig.publish_raw_flags.devStatus,
      onChange: () => togglePublishRawFlag("devStatus"),
    },
    {
      id: "devConfig",
      label: "Configuração do dispositivo",
      icon: <Database className="w-4 h-4" />,
      checked: draftConfig.publish_raw_flags.devConfig,
      onChange: () => togglePublishRawFlag("devConfig"),
    },
    {
      id: "devReset",
      label: "Reset do dispositivo",
      icon: <Database className="w-4 h-4" />,
      checked: draftConfig.publish_raw_flags.devReset,
      onChange: () => togglePublishRawFlag("devReset"),
    },
  ];

  const otherOptions = [
    {
      id: "enable_schedule_resumption",
      label: "Retomada do Agendamento",
      description: "Continua agendamentos após interrupções",
      icon: <RefreshCw className="w-4 h-4" />,
      checked: draftConfig.enable_schedule_resumption,
      onChange: () => changeToggleOptionState("enable_schedule_resumption"),
    },
    {
      id: "debug",
      label: "Modo Debug",
      icon: <Bug className="w-4 h-4" />,
      checked: draftConfig.debug,
      onChange: () => changeToggleOptionState("debug"),
    },
    {
      id: "enable_ferti_resumption",
      label: "Retomada da Fertirrigação",
      icon: <RotateCw className="w-4 h-4" />,
      checked: draftConfig.enable_ferti_resumption,
      onChange: () => changeToggleOptionState("enable_ferti_resumption"),
    },
  ];

  const getPropertyDeviceId = (licStateForDevice: LicState<{ device: string }> | null) => {
    if (!licStateForDevice?.lic?.propertyDeviceId) {
      throw new Error("Este dispositivo não está associado a uma propriedade. Configure-o primeiro.");
    }
    return licStateForDevice?.lic?.propertyDeviceId;
  };

  const handleOpen = () => {
    setIsOpen(true);
    if (enablePolling) {
      console.log("Iniciar polling");
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const buildMetadataPayload = (draftConfig: OptionChanges, currentMetadata: unknown) => {
    let parsedMetadata: Record<string, unknown> = {};

    if (currentMetadata) {
      try {
        parsedMetadata =
          typeof currentMetadata === "string"
            ? (JSON.parse(currentMetadata) as Record<string, unknown>)
            : (currentMetadata as Record<string, unknown>);
      } catch (error) {
        console.warn("Erro ao parsear metadata atual, iniciando novo:", error);
        parsedMetadata = {};
      }
    }

    // Remove any legacy analog_input block so it does not shadow the new
    // table-based assignment. The metadata column itself is kept (shared by
    // other features).
    delete parsedMetadata.analog_input;

    const updatedMeta = {
      ...parsedMetadata,
      id: Date.now(),
      publish_raw_data: encodePublishRawFlags(draftConfig.publish_raw_flags),
      debug: draftConfig.debug,
      enable_schedule_resumption: draftConfig.enable_schedule_resumption,
      enable_ferti_resumption: draftConfig.enable_ferti_resumption,
      max_resumption_attempts: draftConfig.max_resumption_attempts,
    };

    const payload = Object.fromEntries(
      Object.entries({
        metadata: JSON.stringify(updatedMeta),
      })
    );

    return payload;
  };

  /**
   * Persists the analog-input sensor assignment for input #1.
   *
   * - Validates the effective conversion_parameters AND conversion_formula are
   *   non-null (assignment override ?? sensor_model default) before saving.
   * - Closes the previously active input-1 assignment (sets end_date) and
   *   creates a new one. Never writes to property_device.metadata.
   * - When no model is selected, just closes the active assignment (deactivate).
   */
  const saveSensorAssignment = async (targetPropertyDeviceId: string): Promise<void> => {
    setSensorError(null);

    const wantsSensor = sensorDraft.sensorModelId !== "";

    if (wantsSensor) {
      // Effective-null invariant: both effective parameters and formula required.
      if (!effectiveConversion?.formula) {
        setSensorError(
          "A fórmula de conversão é obrigatória. Selecione um modelo com fórmula padrão ou informe uma fórmula de override."
        );
        throw new Error("Configuração de sensor inválida: fórmula de conversão ausente.");
      }
      if (!effectiveConversion.parameters) {
        setSensorError(
          "Os parâmetros de conversão são obrigatórios. Selecione um modelo com parâmetros padrão ou informe parâmetros de override."
        );
        throw new Error("Configuração de sensor inválida: parâmetros de conversão ausentes.");
      }
    }

    // Close the current active input-1 assignment (if any) so the new one
    // doesn't violate the per-input EXCLUDE constraint. Use a single timestamp
    // for both end_date (close) and start_date (create) — half-open ranges
    // [old, ts) and [ts, null) never overlap.
    const ts = new Date().toISOString();

    const currentAssignments = await apiService.propertyDeviceSensorAssignment.query({
      fields: ["id"],
      filter: {
        property_device: { _eq: targetPropertyDeviceId },
        device_input_number: { _eq: CODEC_ANALOG_INPUT_NUMBER },
        end_date: { _null: true },
      },
    });

    for (const a of currentAssignments) {
      await apiService.propertyDeviceSensorAssignment.update(a.id, { end_date: ts });
    }

    if (!wantsSensor) {
      setActiveAssignment(null);
      return;
    }

    const created = await apiService.propertyDeviceSensorAssignment.create({
      property_device: targetPropertyDeviceId,
      sensor_model: sensorDraft.sensorModelId,
      device_input_number: sensorDraft.deviceInputNumber,
      sensor_identifier: sensorDraft.sensorIdentifier.trim() || null,
      start_date: ts,
      conversion_parameters:
        sensorDraft.useOverride && sensorDraft.conversionParameters.trim()
          ? safeParseJson(sensorDraft.conversionParameters)
          : null,
      conversion_formula:
        sensorDraft.useOverride && sensorDraft.conversionFormula.trim() ? sensorDraft.conversionFormula : null,
      conversion_unit:
        sensorDraft.useOverride && sensorDraft.conversionUnit.trim() ? sensorDraft.conversionUnit : null,
    } as any);
    setActiveAssignment(created);
  };

  const handleSaveChanges = async () => {
    if ((!hasChanges && !sensorHasChanges) || isSaving || !licStateForDevice) return;

    // Validate the sensor assignment before any write.
    if (sensorHasChanges) {
      try {
        const targetPropertyDeviceId = getPropertyDeviceId(licStateForDevice);
        // Pre-validate (throws without side effects) — actual save happens below.
        if (sensorDraft.sensorModelId !== "") {
          if (!effectiveConversion?.formula || !effectiveConversion?.parameters) {
            setSensorError(
              !effectiveConversion?.formula
                ? "A fórmula de conversão é obrigatória."
                : "Os parâmetros de conversão são obrigatórios."
            );
            return;
          }
        }
        void targetPropertyDeviceId;
      } catch (err) {
        let errorMessage = "Erro inesperado.";
        if (err instanceof Error) errorMessage = err.message;
        showError({ message: errorMessage });
        return;
      }
    }

    // Check if we are enabling schedule resumption
    const wasEnabled = !!sourceConfig?.enable_schedule_resumption;
    const willBeEnabled = !!draftConfig.enable_schedule_resumption;
    const isEnablingResumption = !wasEnabled && willBeEnabled;

    if (isEnablingResumption) {
      const gapWithResumption = appConfig.irrigation.planMinimumGapMinutes.withResumption;
      const allPlans = plansByLicId(deviceId);

      // Group plans by project
      const plansByProject = new Map<string, PlanScheduleLike[]>();
      for (const plan of allPlans) {
        if (!plan.is_enabled || !plan.start_time || !plan.days_of_week?.length) continue;
        const projectId = plan.project as string;
        if (!projectId) continue;

        const totalDurationSeconds =
          plan.total_irrigation_duration ?? (plan.steps || []).reduce((sum, s) => sum + (s.duration_seconds ?? 0), 0);
        const totalDurationMinutes = Math.max(0, Math.ceil((totalDurationSeconds || 0) / 60));

        const schedule = ensureValidPlanSchedule({
          id: plan.id,
          name: plan.name,
          daysOfWeek: plan.days_of_week,
          startTime: plan.start_time,
          totalDurationMinutes,
        });

        const projectPlans = plansByProject.get(projectId);
        if (projectPlans) {
          projectPlans.push(schedule);
        } else {
          plansByProject.set(projectId, [schedule]);
        }
      }

      // Collect conflicts per project
      const allConflicts: Array<{
        planA: string;
        planB: string;
        day: string;
        gapMinutes: number;
      }> = [];

      for (const plans of plansByProject.values()) {
        const conflicts = findAllPlanConflicts(plans, gapWithResumption);
        for (const c of conflicts) {
          allConflicts.push({
            planA: c.planA.name,
            planB: c.planB.name,
            day: formatDays([c.day]),
            gapMinutes: c.gapMinutes,
          });
        }
      }

      if (allConflicts.length > 0) {
        // Revert the toggle
        setDraftConfig((prev) => ({
          ...prev,
          enable_schedule_resumption: false,
        }));

        // Show conflicts in a modal
        const maxShown = 5;
        const shown = allConflicts.slice(0, maxShown);
        const extra = allConflicts.length - maxShown;

        setConflictsDetails({
          gapWithResumption,
          conflicts: shown,
          extra,
        });

        setShowConflictsModal(true);
        return;
      }
    }

    setIsSaving(true);

    try {
      const targetPropertyDeviceId = getPropertyDeviceId(licStateForDevice);

      // 1) Device config (publish_raw, debug, resumption) via property_device.metadata
      if (hasChanges) {
        const metadataPayload = buildMetadataPayload(draftConfig, metadataConfig);
        try {
          await updatePropertyDevice({
            id: targetPropertyDeviceId,
            data: metadataPayload,
          });
        } catch (err) {
          console.error("Erro ao atualizar metadata:", err);
          throw new Error("Configuração salva, mas falhou ao atualizar metadata.");
        }
      }

      // 2) Analog-input sensor assignment via the new collection (not metadata)
      if (sensorHasChanges) {
        await saveSensorAssignment(targetPropertyDeviceId);
      }

      showSuccess({ message: "Configuração enviada ao dispositivo!" });
      handleClose();
    } catch (err) {
      let errorMessage = "Erro inesperado.";
      if (err instanceof Error) errorMessage = err.message;
      if (typeof err === "string") errorMessage = err;

      console.error("Erro ao salvar configuração: ", err);
      showError({ message: errorMessage });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {trigger(handleOpen)}
      <Modal isOpen={isOpen} onClose={handleClose} title="Configurações Avançadas" size="lg">
        <div className="space-y-6">
          {/* Nota informativa */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
              <div>
                <h4 className="text-amber-800 mb-1 font-bold ">Configurações Avançadas</h4>
                <p className="text-sm text-amber-700">
                  Estas configurações afetam o comportamento interno do dispositivo. Modifique apenas se você tiver
                  conhecimento técnico específico.
                </p>
              </div>
            </div>
          </div>

          {/* Configurações de toggle */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-5 h-5 text-gray-700" />
              <h3 className="font-semibold text-gray-900 text-lg">Configurações de Operação</h3>
            </div>

            {/* Outras configurações */}
            <div className="space-y-4">
              {otherOptions.map((option) => (
                <div key={option.id}>
                  {/* Toggle principal */}
                  <div className="flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg transition-colors border border-gray-100">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="p-2.5 bg-gray-100 rounded-lg text-gray-700">{option.icon}</div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{option.label}</h4>
                        <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                      </div>
                    </div>
                    <Toggle checked={option.checked} disabled={isSaving} onChange={option.onChange} size="md" />
                  </div>

                  {option.id === "enable_schedule_resumption" && draftConfig.enable_schedule_resumption && (
                    <div className="ml-12 mt-2 pr-4 pb-2">
                      <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <div>
                          <span className="text-sm font-medium text-gray-700">Máx. tentativas de retomada</span>
                        </div>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={draftConfig.max_resumption_attempts}
                          onChange={(e) => handleMaxAttemptsChange(parseInt(e.target.value) || 0)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-right"
                          disabled={isSaving}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Configurações de publicação de dados brutos */}
            <div className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Database className="w-5 h-5 text-gray-700" />
                <h3 className="font-semibold text-gray-900 text-lg">Publicar Dados Brutos</h3>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {publishRawOptions.map((option) => (
                  <div
                    key={option.id}
                    className="flex flex-col items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors h-24"
                  >
                    <span className="text-sm font-medium text-gray-700 text-center">{option.label}</span>
                    <Toggle checked={option.checked} disabled={isSaving} onChange={option.onChange} size="sm" />
                  </div>
                ))}
              </div>
            </div>

            {/* Configuração de Entrada Analógica (sensor assignment) */}
            <div className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Activity className="w-5 h-5 text-gray-700" />
                <h3 className="font-semibold text-gray-900 text-lg">Entrada Analógica</h3>
              </div>

              {sensorLoading ? (
                <p className="text-sm text-gray-500">Carregando sensores...</p>
              ) : sensorModels.length === 0 ? (
                <p className="text-sm text-gray-500">
                  Nenhum modelo de sensor cadastrado. Cadastre um sensor_model no painel administrativo.
                </p>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="text-sm font-medium text-gray-700">Sensor</span>
                    <select
                      value={sensorDraft.sensorModelId}
                      onChange={(e) =>
                        setSensorDraft((prev) => ({
                          ...prev,
                          sensorModelId: e.target.value,
                          useOverride: false,
                          conversionParameters: "",
                          conversionFormula: "",
                          conversionUnit: "",
                        }))
                      }
                      className="px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm max-w-[60%]"
                      disabled={isSaving}
                    >
                      <option value="">Nenhum</option>
                      {sensorModels.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.type} — {m.model}
                          {m.conversion_unit ? ` (${m.conversion_unit})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  {sensorDraft.sensorModelId !== "" && (
                    <>
                      {selectedSensorModel && (
                        <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <span className="font-medium text-gray-600">Conversão padrão do modelo:</span>{" "}
                          {selectedSensorModel.conversion_formula
                            ? `fórmula definida`
                            : "sem fórmula"}{" "}
                          · {selectedSensorModel.conversion_parameters ? "parâmetros definidos" : "sem parâmetros"}
                          {selectedSensorModel.conversion_unit ? ` · ${selectedSensorModel.conversion_unit}` : ""}
                        </div>
                      )}

                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <span className="text-sm font-medium text-gray-700">
                          Usar conversão personalizada (override)
                        </span>
                        <Toggle
                          checked={sensorDraft.useOverride}
                          disabled={isSaving}
                          onChange={() => setSensorDraft((prev) => ({ ...prev, useOverride: !prev.useOverride }))}
                          size="sm"
                        />
                      </div>

                      {sensorDraft.useOverride && (
                        <div className="space-y-3">
                          <div>
                            <label className="text-xs font-medium text-gray-600">Parâmetros (JSON)</label>
                            <textarea
                              value={sensorDraft.conversionParameters}
                              onChange={(e) =>
                                setSensorDraft((prev) => ({ ...prev, conversionParameters: e.target.value }))
                              }
                              placeholder='{"min_signal_mv":0,"max_signal_mv":2500,"min_value":0,"max_value":2000}'
                              className="w-full mt-1 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-xs font-mono"
                              rows={2}
                              disabled={isSaving}
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-600">Fórmula (corpo de função JS)</label>
                            <textarea
                              value={sensorDraft.conversionFormula}
                              onChange={(e) =>
                                setSensorDraft((prev) => ({ ...prev, conversionFormula: e.target.value }))
                              }
                              placeholder="return parameters.min_value + (analog_voltage - parameters.min_signal_mv) * ...;"
                              className="w-full mt-1 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-xs font-mono"
                              rows={2}
                              disabled={isSaving}
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-600">Unidade</label>
                            <input
                              type="text"
                              value={sensorDraft.conversionUnit}
                              onChange={(e) =>
                                setSensorDraft((prev) => ({ ...prev, conversionUnit: e.target.value }))
                              }
                              placeholder="W/m²"
                              className="w-full mt-1 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                              disabled={isSaving}
                            />
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <span className="text-sm font-medium text-gray-700">Identificador do sensor (opcional)</span>
                        <input
                          type="text"
                          value={sensorDraft.sensorIdentifier}
                          onChange={(e) =>
                            setSensorDraft((prev) => ({ ...prev, sensorIdentifier: e.target.value }))
                          }
                          placeholder="Nº de série"
                          className="w-40 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm text-right"
                          disabled={isSaving}
                        />
                      </div>
                    </>
                  )}

                  {sensorError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                      {sensorError}
                    </div>
                  )}

                  <p className="text-xs text-gray-500">
                    O CODEC possui uma única entrada analógica (entrada 1). A atribuição substitui a configuração
                    anterior ao salvar.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              variant="secondary"
              disabled={isSaving}
              onClick={handleClose}
              className="flex-1 border-red-600 text-red-600 hover:bg-red-50 active:bg-red-100"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveChanges}
              disabled={isSaving || (!hasChanges && !sensorHasChanges)}
              loading={isSaving}
              variant="primary"
              className="flex-1 text-white"
            >
              Salvar Alterações
            </Button>
          </div>
        </div>
      </Modal>

      {/* Conflicts modal: shown when enabling resumption with too-close plans */}
      {conflictsDetails && (
        <Modal
          isOpen={showConflictsModal}
          onClose={() => setShowConflictsModal(false)}
          title="Conflito ao ativar Retomada do Agendamento"
          size="md"
        >
          <div className="space-y-4 text-sm">
            {/* Warning header */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
              <div className="text-amber-900">
                <p className="font-semibold">Operação bloqueada</p>
                <p className="mt-1 text-amber-800">
                  Não é possível ativar a Retomada do Agendamento porque existem planos muito próximos entre si. É
                  necessário manter pelo menos {conflictsDetails.gapWithResumption} minutos de intervalo.
                </p>
              </div>
            </div>

            {/* Conflicts list */}
            <div className="bg-amber-50/60 border border-amber-200 rounded-xl p-4">
              <p className="font-medium text-amber-900 mb-2">Conflitos detectados:</p>
              <ul className="list-disc list-inside space-y-1 text-amber-900">
                {conflictsDetails.conflicts.map((c, index) => (
                  <li key={index}>
                    Conflito entre &quot;{c.planA}&quot; e &quot;{c.planB}&quot; em {c.day} (intervalo: {c.gapMinutes}{" "}
                    min).
                  </li>
                ))}
              </ul>
              {conflictsDetails.extra > 0 && (
                <p className="mt-2 text-amber-800">+{conflictsDetails.extra} outros conflitos.</p>
              )}
            </div>

            {/* Instruction */}
            <p className="text-amber-900">
              Ajuste os horários dos planos acima e tente ativar a Retomada do Agendamento novamente.
            </p>

            <div className="flex justify-end pt-2">
              <Button
                variant="primary"
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => setShowConflictsModal(false)}
              >
                Entendi
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

export default AdvanceCodekConfig;

// ---------------------------------------------------------------------------
// Sensor assignment helpers (module-local)
// ---------------------------------------------------------------------------

/** Builds a sensor draft from an existing active assignment (or empty). */
function buildDraftFromAssignment(
  assignment: PropertyDeviceSensorAssignment | null,
  models: SensorModel[]
): SensorDraft {
  if (!assignment) {
    return { ...EMPTY_SENSOR_DRAFT };
  }
  const hasOverride =
    assignment.conversion_parameters != null ||
    assignment.conversion_formula != null ||
    assignment.conversion_unit != null;
  return {
    sensorModelId: typeof assignment.sensor_model === "string" ? assignment.sensor_model : "",
    deviceInputNumber: assignment.device_input_number ?? CODEC_ANALOG_INPUT_NUMBER,
    sensorIdentifier: assignment.sensor_identifier ?? "",
    useOverride: hasOverride,
    conversionParameters:
      assignment.conversion_parameters != null ? JSON.stringify(assignment.conversion_parameters) : "",
    conversionFormula: assignment.conversion_formula ?? "",
    conversionUnit: assignment.conversion_unit ?? "",
  };
}

/** Structural equality for sensor drafts (drives the dirty check). */
function draftsEqual(a: SensorDraft, b: SensorDraft): boolean {
  return (
    a.sensorModelId === b.sensorModelId &&
    a.deviceInputNumber === b.deviceInputNumber &&
    a.sensorIdentifier === b.sensorIdentifier &&
    a.useOverride === b.useOverride &&
    a.conversionParameters === b.conversionParameters &&
    a.conversionFormula === b.conversionFormula &&
    a.conversionUnit === b.conversionUnit
  );
}

/** Parses a JSON string to an object, returning null on failure/empty. */
function safeParseJson(value: string): Record<string, unknown> | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}
